import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useChat from '../../hooks/use-chat';
import { isLiveChatWithinOperatingHours } from '../../lib/live-chat-availability';
import { cn, isNovartisInstance, trimQuery } from '../../lib/utils';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';

interface NovartisLiveChatButtonProps {
  align?: 'left' | 'center';
  className?: string;
  inline?: boolean;
  onStart?: () => void;
  query?: string;
  size?: 'default' | 'sm';
}

function NovartisLiveChatButton(props: NovartisLiveChatButtonProps) {
  const { t } = useTranslation();
  const { liveChat } = useChat();
  const [open, setOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');

  if (!isNovartisInstance() || !liveChat.enabled) {
    return null;
  }

  const isBusy = liveChat.status === 'preparing' || liveChat.status === 'routing';
  const isRunning = liveChat.status === 'running';
  const label = isRunning
    ? t('chat.live_chat.button_running')
    : isBusy
      ? t('chat.live_chat.button_connecting')
      : t('chat.live_chat.button_start');

  function handleStart(name: string) {
    if (!liveChat.canStart || !isLiveChatWithinOperatingHours()) {
      return;
    }

    setOpen(false);
    props.onStart?.();
    void liveChat.start(trimQuery(props.query || ''), trimQuery(name));
    setCustomerName('');
  }

  const button = (
    <Button
      type="button"
      variant="outline"
      size={props.size}
      className={cn(
        'border-[hsl(var(--xircle-secondary))] text-[hsl(var(--xircle-main))] disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:opacity-100',
        props.className
      )}
      disabled={!liveChat.canStart}
      onClick={() => setOpen(true)}
    >
      {label}
    </Button>
  );

  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-[420px] overflow-y-auto rounded-2xl border border-[#d8d8dc] bg-[#F2F2F4] p-5 shadow-[0_18px_50px_rgba(0,32,104,0.16)]">
        <DialogHeader>
          <DialogTitle className="text-[#002068]">
            {t('chat.live_chat.name_dialog_title')}
          </DialogTitle>
          <DialogDescription className="text-[#002068]">
            {t('chat.live_chat.name_hint')}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            handleStart(customerName);
          }}
        >
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700" htmlFor="live-chat-name">
              {t('chat.live_chat.name_label')}
            </label>
            <Input
              id="live-chat-name"
              value={customerName}
              maxLength={100}
              onChange={(event) => setCustomerName(event.target.value)}
              placeholder={t('chat.live_chat.name_placeholder')}
              className="border-[#d8d8dc] bg-white text-[#13233a] focus-visible:ring-[#0460A9]"
            />
          </div>
          <p className="text-sm text-[#002068]">{t('chat.live_chat.availability_hint')}</p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              className="text-[#002068] hover:bg-white/70"
              disabled={!liveChat.canStart}
              onClick={() => handleStart('')}
            >
              {t('chat.live_chat.name_skip')}
            </Button>
            <Button
              type="submit"
              className="bg-[#0460A9] text-white hover:bg-[#002068]"
              disabled={!liveChat.canStart}
            >
              {t('chat.live_chat.button_start')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  if (props.inline) {
    return (
      <>
        {button}
        {dialog}
      </>
    );
  }

  return (
    <div className={cn('mt-4 flex', props.align === 'center' ? 'justify-center' : 'justify-start')}>
      {button}
      {dialog}
    </div>
  );
}

export default NovartisLiveChatButton;
